import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { 
  Brain, 
  Settings, 
  Activity, 
  Target, 
  Zap, 
  TrendingUp,
  BarChart3,
  RefreshCw,
  Play,
  Pause,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';

interface AIModel {
  id: string;
  name: string;
  type: 'extraction' | 'classification' | 'validation' | 'anomaly';
  status: 'active' | 'inactive' | 'training' | 'error';
  accuracy: number;
  confidence: number;
  lastTrained: Date;
  performance: Array<{ date: string; accuracy: number; confidence: number }>;
  config: {
    temperature: number;
    maxTokens: number;
    threshold: number;
    enableTuning: boolean;
  };
}

export const AIModelManager = () => {
  const [selectedModel, setSelectedModel] = useState<string>('extraction-v1');
  const [isTraining, setIsTraining] = useState(false);

  // Mock AI models data
  const models: AIModel[] = [
    {
      id: 'extraction-v1',
      name: 'Bill Data Extractor v1.2',
      type: 'extraction',
      status: 'active',
      accuracy: 94.2,
      confidence: 89.5,
      lastTrained: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      performance: [
        { date: '2024-01-01', accuracy: 91.2, confidence: 85.3 },
        { date: '2024-01-15', accuracy: 92.8, confidence: 87.1 },
        { date: '2024-02-01', accuracy: 93.5, confidence: 88.2 },
        { date: '2024-02-15', accuracy: 94.2, confidence: 89.5 },
      ],
      config: {
        temperature: 0.2,
        maxTokens: 2048,
        threshold: 0.85,
        enableTuning: true,
      }
    },
    {
      id: 'classifier-v1',
      name: 'Bill Type Classifier',
      type: 'classification',
      status: 'active',
      accuracy: 96.8,
      confidence: 92.1,
      lastTrained: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
      performance: [
        { date: '2024-01-01', accuracy: 94.1, confidence: 89.8 },
        { date: '2024-01-15', accuracy: 95.2, confidence: 90.5 },
        { date: '2024-02-01', accuracy: 96.1, confidence: 91.3 },
        { date: '2024-02-15', accuracy: 96.8, confidence: 92.1 },
      ],
      config: {
        temperature: 0.1,
        maxTokens: 1024,
        threshold: 0.9,
        enableTuning: true,
      }
    },
    {
      id: 'validator-v1',
      name: 'Data Validator',
      type: 'validation',
      status: 'training',
      accuracy: 88.7,
      confidence: 84.2,
      lastTrained: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      performance: [
        { date: '2024-01-01', accuracy: 85.3, confidence: 81.2 },
        { date: '2024-01-15', accuracy: 86.9, confidence: 82.8 },
        { date: '2024-02-01', accuracy: 87.8, confidence: 83.5 },
        { date: '2024-02-15', accuracy: 88.7, confidence: 84.2 },
      ],
      config: {
        temperature: 0.3,
        maxTokens: 1536,
        threshold: 0.8,
        enableTuning: false,
      }
    },
    {
      id: 'anomaly-v1',
      name: 'Anomaly Detector',
      type: 'anomaly',
      status: 'active',
      accuracy: 91.5,
      confidence: 87.8,
      lastTrained: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
      performance: [
        { date: '2024-01-01', accuracy: 88.2, confidence: 84.1 },
        { date: '2024-01-15', accuracy: 89.7, confidence: 85.6 },
        { date: '2024-02-01', accuracy: 90.8, confidence: 86.9 },
        { date: '2024-02-15', accuracy: 91.5, confidence: 87.8 },
      ],
      config: {
        temperature: 0.4,
        maxTokens: 1024,
        threshold: 0.75,
        enableTuning: true,
      }
    }
  ];

  const currentModel = models.find(m => m.id === selectedModel) || models[0];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'training': return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'error': return <AlertCircle className="h-4 w-4 text-red-500" />;
      default: return <Pause className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'training': return 'default';
      case 'error': return 'destructive';
      default: return 'secondary';
    }
  };

  const handleTrainModel = () => {
    setIsTraining(true);
    setTimeout(() => {
      setIsTraining(false);
    }, 3000);
  };

  const chartConfig = {
    accuracy: {
      label: "Accuracy %",
      color: "hsl(var(--chart-1))",
    },
    confidence: {
      label: "Confidence %",
      color: "hsl(var(--chart-2))",
    },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Brain className="h-6 w-6 text-primary" />
            AI Model Manager
          </h2>
          <p className="text-muted-foreground">
            Configure and monitor AI models for optimal bill processing performance
          </p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={handleTrainModel} disabled={isTraining}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isTraining ? 'animate-spin' : ''}`} />
            {isTraining ? 'Training...' : 'Train Models'}
          </Button>
          <Button>
            <Settings className="h-4 w-4 mr-2" />
            Global Settings
          </Button>
        </div>
      </div>

      {/* Model Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {models.map((model) => (
          <Card key={model.id} className={`cursor-pointer transition-all hover:shadow-md ${selectedModel === model.id ? 'ring-2 ring-primary' : ''}`} onClick={() => setSelectedModel(model.id)}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium truncate">{model.name}</CardTitle>
              {getStatusIcon(model.status)}
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">Accuracy</span>
                  <span className="text-sm font-medium">{model.accuracy}%</span>
                </div>
                <Progress value={model.accuracy} className="h-2" />
                <Badge variant={getStatusBadge(model.status)} className="text-xs">
                  {model.status.toUpperCase()}
                </Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Model Details */}
      <Tabs defaultValue="performance" className="space-y-4">
        <TabsList>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="configuration">Configuration</TabsTrigger>
          <TabsTrigger value="training">Training</TabsTrigger>
          <TabsTrigger value="deployment">Deployment</TabsTrigger>
        </TabsList>

        <TabsContent value="performance" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>{currentModel.name} - Performance Trends</CardTitle>
                  <CardDescription>
                    Accuracy and confidence metrics over time
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ChartContainer config={chartConfig} className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={currentModel.performance}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis domain={[80, 100]} />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Line
                          type="monotone"
                          dataKey="accuracy"
                          stroke={chartConfig.accuracy.color}
                          strokeWidth={2}
                          dot={{ fill: chartConfig.accuracy.color, r: 4 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="confidence"
                          stroke={chartConfig.confidence.color}
                          strokeWidth={2}
                          dot={{ fill: chartConfig.confidence.color, r: 4 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Current Metrics</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Accuracy Score</span>
                    <div className="flex items-center gap-2">
                      <Progress value={currentModel.accuracy} className="w-16 h-2" />
                      <span className="text-sm font-medium">{currentModel.accuracy}%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Confidence Level</span>
                    <div className="flex items-center gap-2">
                      <Progress value={currentModel.confidence} className="w-16 h-2" />
                      <span className="text-sm font-medium">{currentModel.confidence}%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Status</span>
                    <Badge variant={getStatusBadge(currentModel.status)}>
                      {currentModel.status.toUpperCase()}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Last Trained</span>
                    <span className="text-sm font-medium">
                      {currentModel.lastTrained.toLocaleDateString()}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button className="w-full" variant="outline">
                    <Play className="h-4 w-4 mr-2" />
                    Deploy Model
                  </Button>
                  <Button className="w-full" variant="outline">
                    <Pause className="h-4 w-4 mr-2" />
                    Pause Model
                  </Button>
                  <Button className="w-full" variant="outline">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Retrain Model
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="configuration" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Model Configuration</CardTitle>
                <CardDescription>
                  Adjust model parameters for optimal performance
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="temperature">Temperature: {currentModel.config.temperature}</Label>
                  <Slider
                    id="temperature"
                    min={0}
                    max={1}
                    step={0.1}
                    value={[currentModel.config.temperature]}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    Controls randomness in model output (0 = deterministic, 1 = creative)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="maxTokens">Max Tokens</Label>
                  <Input
                    id="maxTokens"
                    type="number"
                    value={currentModel.config.maxTokens}
                    min={512}
                    max={4096}
                  />
                  <p className="text-xs text-muted-foreground">
                    Maximum number of tokens in the model response
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="threshold">Confidence Threshold: {currentModel.config.threshold}</Label>
                  <Slider
                    id="threshold"
                    min={0.5}
                    max={1}
                    step={0.05}
                    value={[currentModel.config.threshold]}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    Minimum confidence required for automatic processing
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="autoTuning">Auto-tuning</Label>
                    <p className="text-xs text-muted-foreground">
                      Automatically optimize model parameters
                    </p>
                  </div>
                  <Switch
                    id="autoTuning"
                    checked={currentModel.config.enableTuning}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Advanced Settings</CardTitle>
                <CardDescription>
                  Fine-tune model behavior and performance
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="modelType">Model Type</Label>
                  <Select value={currentModel.type}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="extraction">Data Extraction</SelectItem>
                      <SelectItem value="classification">Classification</SelectItem>
                      <SelectItem value="validation">Validation</SelectItem>
                      <SelectItem value="anomaly">Anomaly Detection</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="updateFreq">Update Frequency</Label>
                  <Select defaultValue="weekly">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="manual">Manual Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="priority">Processing Priority</Label>
                  <Select defaultValue="normal">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button className="w-full">
                  <Settings className="h-4 w-4 mr-2" />
                  Save Configuration
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="training" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Model Training</CardTitle>
              <CardDescription>
                Monitor training progress and schedule retraining sessions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <Brain className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Training Module</h3>
                <p className="text-muted-foreground mb-4">
                  Advanced training capabilities coming soon
                </p>
                <Button onClick={handleTrainModel} disabled={isTraining}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${isTraining ? 'animate-spin' : ''}`} />
                  {isTraining ? 'Training in Progress...' : 'Start Training'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deployment" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Model Deployment</CardTitle>
              <CardDescription>
                Manage model deployment and versioning
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <Zap className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Deployment Pipeline</h3>
                <p className="text-muted-foreground mb-4">
                  Automated deployment and rollback capabilities
                </p>
                <Button>
                  <Play className="h-4 w-4 mr-2" />
                  Deploy to Production
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};